import { ApiV1_Product, ApiV1_ProductOptionItem } from '@pedix-workspace/api-v1';
import { StockInfo } from '@pedix-workspace/shared-stock';
import {
  DEFAULT_LANGUAGE,
  getPropInSelectedLanguage,
  SUPPORTED_LANGUAGES,
} from '@pedix-workspace/utils';

export type StockErrorItem = {
  name: string;
  parentName?: string;
  requested: number;
  available: number;
};

/**
 * Resuelve los nombres de cada `StockInfo` desde el catálogo (por id, según `entityType`): el
 * `StockInfo.item` es un `ProductStock` y ya no lleva `name`/`parentItem`. Una presentación resuelve
 * su nombre dentro del producto padre (`productId`); un option-item resuelve su nombre en
 * `product.options[].items[]` (embebidos + shared ya mergeados por el catálogo) — su `productId` es
 * el producto contenedor (embebido) o self-id (shared), así que se busca por id de entidad, no por
 * `productId`. Un producto top-level resuelve directo por `productId` (== id de la entidad).
 */
export function buildStockErrorViews(
  stockErrors: StockInfo[],
  products: ApiV1_Product[],
  selectedLanguage: SUPPORTED_LANGUAGES = DEFAULT_LANGUAGE,
): StockErrorItem[] {
  const productById = new Map(products.map(product => [product.id, product]));
  const optionItemById = new Map<string, { item: ApiV1_ProductOptionItem; parent: ApiV1_Product }>();
  for (const product of products) {
    for (const option of product.options ?? []) {
      for (const optionItem of option.items ?? []) {
        optionItemById.set(optionItem.id, { item: optionItem, parent: product });
      }
    }
  }

  return stockErrors.map(stockError => {
    const item = stockError.item;
    const base = { requested: stockError.totalStockInCart, available: item?.stockQty ?? 0 };

    if (!item) {
      return { ...base, name: '' };
    }

    if (item.entityType === 'presentation') {
      const parent = productById.get(item.productId);
      const presentation = parent?.presentations?.items?.find(
        presentationItem => presentationItem.id === item.id,
      );

      return {
        ...base,
        name: presentation
          ? getPropInSelectedLanguage(presentation, 'name', selectedLanguage, true)
          : '',
        parentName: parent
          ? getPropInSelectedLanguage(parent, 'name', selectedLanguage, true)
          : undefined,
      };
    }

    if (item.entityType === 'option-item') {
      const optionEntry = optionItemById.get(item.id);

      if (optionEntry) {
        return {
          ...base,
          name: getPropInSelectedLanguage(optionEntry.item, 'name', selectedLanguage, true),
          parentName: getPropInSelectedLanguage(optionEntry.parent, 'name', selectedLanguage, true),
        };
      }

      // Fallback: shared option-item que existe como producto top-level (`isOptionItem`, `productId`
      // == su propio id) sin aparecer mergeado en `options` de ningún producto del cart.
      const selfProduct = productById.get(item.productId);

      return {
        ...base,
        name: selfProduct
          ? getPropInSelectedLanguage(selfProduct, 'name', selectedLanguage, true)
          : '',
      };
    }

    const product = productById.get(item.productId);

    return {
      ...base,
      name: product ? getPropInSelectedLanguage(product, 'name', selectedLanguage, true) : '',
    };
  });
}
